/**
 * Delegated authority: who must approve a decision, from rules an admin keeps
 * in Admin → Approvals. Every decision goes through them: a lever proposed in
 * Finance, a director decision, a written resolution. Rules are read top to
 * bottom and the first that matches decides, on four things:
 *
 * - the decision's type (a rule can cover some types, or all of them);
 * - the amount: for a lever, the most money it moves either way over the next
 *   twelve months (a deferral moves what it holds back and repays, though it
 *   nets to nothing); for other decisions, the amount entered, if any. A rule
 *   with an amount range never matches a decision without an amount;
 * - the gate when it's proposed, on the approved forecast without it;
 * - whether it's a related party: it touches a line whose supplier is
 *   connected to a director, or is marked connected itself. Those directors are
 *   stepped aside: they never approve it, whatever the rule says.
 *
 * A rule names its approvers, or leaves them empty for every director, and
 * needs all of them, a majority, or any one. When no rule matches, or nobody
 * named can approve, any director who isn't stepped aside can.
 */
import { addDays, type ISODate } from "./dates";
import { occurrences, type GateState, type LeverInput } from "./engine";
import type { Commitment, Role } from "./types";

export type RelatedPartyCondition = "any" | "yes" | "no";
export type ApprovalMode = "any" | "majority" | "all";
export const APPROVAL_MODE_LABEL: Record<ApprovalMode, string> = { all: "All of them", majority: "A majority", any: "Any one" };

export interface AuthorityRule {
  id: string;
  /** Why: shown with the requirement, e.g. "Green: Stuart approves up to £1,000". */
  label: string;
  /** Decision types it covers; empty for any. */
  typeIds: string[];
  /** Gate states it covers; empty for any. */
  gateStates: GateState[];
  /** Pence, exclusive: the amount must be over this. Null for no lower bound. */
  over: number | null;
  /** Pence, inclusive: the amount must be at most this. Null for no upper bound. */
  upTo: number | null;
  relatedParty: RelatedPartyCondition;
  /** People who approve; empty for every director. */
  approverIds: string[];
  mode: ApprovalMode;
  active: boolean;
  sortOrder: number;
}

/** Who must approve a lever, worked out when it was proposed. */
export interface ApprovalRequirement {
  ruleId: string | null;
  label: string;
  approverIds: string[];
  mode: ApprovalMode;
  /** Pence; null for a decision without an amount. */
  amount: number | null;
  gate: GateState;
  /** Directors connected to it, stepped aside. */
  recusedIds: string[];
  /** No rule matched, or nobody it named could approve: any director not stepped aside. */
  fallback: boolean;
}

export interface ApprovalDecision {
  userId: string;
  decision: "approve" | "reject";
  note: string | null;
  at: string;
}

export interface Person {
  id: string;
  role: Role;
  active: boolean;
}

/** The most money a lever moves either way over the twelve months after `asOf` (pence). */
export function approvalAmount(lever: Pick<LeverInput, "entries">, asOf: ISODate): number {
  const end = addDays(asOf, 365);
  let inn = 0;
  let out = 0;
  for (const e of lever.entries) {
    const n = occurrences(e, asOf, end).length;
    if (e.amount > 0) inn += e.amount * n;
    else out -= e.amount * n;
  }
  return Math.max(inn, out);
}

/** Directors connected to a lever: through the lines it's against, or marked on the lever itself. */
export function relatedDirectors(lever: { entries: { commitmentId: string | null }[]; connectedDirectorId?: string | null }, commitments: Pick<Commitment, "id" | "connectedDirectorId">[]): string[] {
  const byLine = new Map(commitments.map((c) => [c.id, c.connectedDirectorId]));
  const out = new Set<string>();
  if (lever.connectedDirectorId) out.add(lever.connectedDirectorId);
  for (const e of lever.entries) {
    const d = e.commitmentId ? byLine.get(e.commitmentId) : null;
    if (d) out.add(d);
  }
  return [...out];
}

export interface ApprovalCase {
  amount: number | null;
  gate: GateState;
  recusedIds: string[];
  typeId?: string | null;
}

export function ruleMatches(r: AuthorityRule, c: Omit<ApprovalCase, "recusedIds">, related: boolean): boolean {
  const { amount, gate } = c;
  if (!r.active) return false;
  if (r.typeIds.length && !(c.typeId && r.typeIds.includes(c.typeId))) return false;
  if (r.gateStates.length && !r.gateStates.includes(gate)) return false;
  if ((r.over !== null || r.upTo !== null) && amount === null) return false;
  if (r.over !== null && !(amount! > r.over)) return false;
  if (r.upTo !== null && !(amount! <= r.upTo)) return false;
  if (r.relatedParty === "yes" && !related) return false;
  if (r.relatedParty === "no" && related) return false;
  return true;
}

export function requirementFor(p: ApprovalCase, rules: AuthorityRule[], people: Person[]): ApprovalRequirement {
  const recused = new Set(p.recusedIds);
  const can = (id: string) => !recused.has(id) && people.some((u) => u.id === id && u.active && u.role !== "observer");
  const directors = people.filter((u) => u.role === "director" && u.active && !recused.has(u.id)).map((u) => u.id);
  const rule = [...rules].sort((a, b) => a.sortOrder - b.sortOrder).find((r) => ruleMatches(r, p, p.recusedIds.length > 0));
  const base = { amount: p.amount, gate: p.gate, recusedIds: p.recusedIds };
  if (rule) {
    const named = rule.approverIds.length ? rule.approverIds.filter(can) : directors;
    if (named.length) return { ...base, ruleId: rule.id, label: rule.label, approverIds: named, mode: rule.mode, fallback: false };
  }
  return { ...base, ruleId: rule?.id ?? null, label: rule ? `${rule.label} (nobody it names can approve)` : "No rule covers this", approverIds: directors, mode: "any", fallback: true };
}

export interface ApprovalProgress {
  approved: boolean;
  rejected: boolean;
  /** Required approvers who haven't decided yet. */
  waiting: string[];
}

/** How many approvals a requirement needs: one, more than half, or everyone. */
export function approvalsNeeded(req: Pick<ApprovalRequirement, "approverIds" | "mode">): number {
  const n = req.approverIds.length;
  return req.mode === "any" ? Math.min(1, n) : req.mode === "majority" ? Math.floor(n / 2) + 1 : n;
}

/**
 * Where a decision stands. Any one or all of them: the first "not approved"
 * decides. A majority: approved once more than half have approved, and not
 * approved once enough have said no that a majority can't be reached.
 */
export function approvalProgress(req: ApprovalRequirement, decisions: ApprovalDecision[]): ApprovalProgress {
  const mine = decisions.filter((d) => req.approverIds.includes(d.userId));
  const yes = new Set(mine.filter((d) => d.decision === "approve").map((d) => d.userId));
  const no = new Set(mine.filter((d) => d.decision === "reject").map((d) => d.userId));
  const need = approvalsNeeded(req);
  const rejected = req.mode === "majority" ? no.size > req.approverIds.length - need : no.size > 0;
  const approved = !rejected && yes.size >= need;
  return { approved, rejected, waiting: approved || rejected ? [] : req.approverIds.filter((id) => !yes.has(id) && !no.has(id)) };
}
